"use client";

/**
 * Site-wide analytics (Phase 20): page views on every route change, clicks on
 * links and buttons, the cookie banner, and the third-party tags the visitor
 * allowed. See lib/track.ts (our own analytics) and lib/pixels.ts (PostHog
 * and ad pixels).
 */

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Cookie, ShieldCheck, X } from "lucide-react";
import { getToken } from "@/lib/api";
import { applyConsent, getConfig, pixelsEvent, pixelsIdentify, pixelsPageview } from "@/lib/pixels";
import { click, flushNow, getConsent, onConsent, pageview, setConsent, track } from "@/lib/track";

function userIdFromToken(): string | null {
  const t = getToken();
  if (!t || t.startsWith("rbk_")) return null;
  try {
    const part = t.split(".")[1];
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.sub === "string" ? json.sub : null;
  } catch {
    return null;
  }
}

const CLICKABLE = "a,button,[role=button],[role=tab],[role=menuitem],summary,[data-track],input[type=submit],input[type=button]";

function labelOf(el: HTMLElement): string {
  const explicit = el.getAttribute("data-track") || el.getAttribute("aria-label") || el.getAttribute("title");
  if (explicit) return explicit.trim();
  const text = (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
  if (text) return text.slice(0, 120);
  const img = el.querySelector("img[alt]") as HTMLImageElement | null;
  if (img?.alt) return img.alt;
  return (el as HTMLAnchorElement).href || el.tagName.toLowerCase();
}

export function Analytics() {
  const pathname = usePathname();
  const [banner, setBanner] = useState(false);
  const [custom, setCustom] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: true, marketing: true });
  const [recordClicks, setRecordClicks] = useState(true);

  // the banner, tags for an earlier choice, and "Cookie settings" links
  useEffect(() => {
    let alive = true;
    getConfig().then((cfg) => {
      if (!alive) return;
      const showBanner = cfg?.tracking?.consent_banner !== false;
      setRecordClicks(cfg?.tracking?.record_clicks !== false);
      if (!getConsent() && showBanner) setBanner(true);
    }).catch(() => {
      // settings unreachable: behave as before they existed (ask for consent)
      if (alive && !getConsent()) setBanner(true);
    });
    void applyConsent();
    const off = onConsent((c) => {
      void applyConsent(c);
      pixelsIdentify(userIdFromToken());
    });
    const open = () => {
      const c = getConsent();
      setPrefs({ analytics: c?.analytics ?? true, marketing: c?.marketing ?? true });
      setCustom(true);
      setBanner(true);
    };
    window.addEventListener("rb:cookie-settings", open);
    const onEvent = (e: Event) => {
      const d = (e as CustomEvent).detail as { name: string; props: Record<string, unknown> };
      void pixelsEvent(d.name, { ...d.props, user_id: d.props.user_id ?? userIdFromToken() });
    };
    window.addEventListener("rb:event", onEvent);
    const hide = () => flushNow();
    window.addEventListener("pagehide", hide);
    return () => {
      alive = false;
      off();
      window.removeEventListener("rb:cookie-settings", open);
      window.removeEventListener("rb:event", onEvent);
      window.removeEventListener("pagehide", hide);
    };
  }, []);

  // a page view on every route change
  useEffect(() => {
    pageview();
    void pixelsPageview();
    pixelsIdentify(userIdFromToken());
  }, [pathname]);

  // clicks on links and buttons (only the visible label and the link address; never typed text)
  useEffect(() => {
    if (!recordClicks) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const el = target?.closest?.(CLICKABLE) as HTMLElement | null;
      if (!el || el.closest("[data-no-track]")) return;
      const href = (el as HTMLAnchorElement).href || el.getAttribute("data-href") || "";
      click(labelOf(el), href);
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [recordClicks]);

  // Paddle's checkout opening (its overlay iframe appears). Watched from here so the
  // billing code itself stays untouched.
  useEffect(() => {
    const isPaddle = (n: Node) =>
      n instanceof HTMLIFrameElement && /paddle/i.test(`${n.className} ${n.name} ${n.src}`);
    const obs = new MutationObserver((records) => {
      for (const r of records) {
        for (const n of Array.from(r.addedNodes)) {
          if (isPaddle(n) || (n instanceof HTMLElement && Array.from(n.querySelectorAll("iframe")).some(isPaddle))) {
            track("checkout_started", { page: window.location.pathname });
            return;
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, []);

  function choose(analytics: boolean, marketing: boolean) {
    // close first, so nothing that follows can keep the banner on screen
    setBanner(false);
    setCustom(false);
    try {
      setConsent(analytics, marketing);
    } catch {
      /* the choice still holds for this visit */
    }
    // the current page, now with the visitor's choice (after the banner has gone)
    setTimeout(() => {
      try {
        pageview();
      } catch {
        /* ignore */
      }
    }, 0);
  }

  if (!banner) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-[115] px-3 pb-[max(12px,env(safe-area-inset-bottom))] sm:bottom-6 sm:left-1/2 sm:right-auto sm:w-[calc(100%-48px)] sm:max-w-[760px] sm:-translate-x-1/2 sm:px-0 sm:pb-0" role="dialog"
      aria-modal="false" aria-labelledby="rb-cookie-title" data-no-track>
      <div className={`rounded-[20px] border border-black/5 bg-white p-3.5 shadow-[0_24px_60px_-20px_rgba(20,15,51,0.45)] sm:rounded-[22px] sm:p-6 ${custom ? "rb-scroll max-h-[min(72dvh,640px)] overflow-y-auto overscroll-contain" : ""}`}>
        <div className="flex items-start gap-3">
          <span className="hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#B49CFF] to-[#6a52ff] text-white sm:flex">
            <Cookie size={19} />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="rb-cookie-title" className="flex items-center gap-1.5 text-[14px] font-extrabold text-[#140f33] sm:text-[15px]">
              <Cookie size={15} className="text-[#6a52ff] sm:hidden" aria-hidden /> Cookies & privacy
            </h2>
            <p className="mt-1 text-[12px] leading-snug text-[#5c5878] sm:text-[13px] sm:leading-relaxed">
              <span className="sm:hidden">We keep you signed in, and with your OK measure visits and ads.{" "}</span>
              <span className="hidden sm:inline">We use essential storage to keep you signed in. With your permission we also measure visits to improve Rankbist and see which ads work.{" "}</span>
              <Link href="/cookies" className="font-semibold text-[#5a3fe6] underline underline-offset-2">Cookie policy</Link>
            </p>
          </div>
          {custom && (
            <button onClick={() => { setBanner(false); setCustom(false); }} className="-mr-1 -mt-1 rounded-lg p-1.5 text-[#8d89a6] hover:bg-[#f4f2fb]" aria-label="Close">
              <X size={16} />
            </button>
          )}
        </div>

        {custom && (
          <ul className="mt-4 flex flex-col gap-2">
            {[
              { key: "essential", title: "Essential", body: "Sign-in, security and your choices here. Always on.", locked: true },
              { key: "analytics", title: "Analytics", body: "Which pages and buttons people use (Rankbist analytics, PostHog, Google Analytics, Clarity)." },
              { key: "marketing", title: "Marketing", body: "Measuring our ads on Meta, Google, TikTok, LinkedIn and X." },
            ].map((row) => {
              const on = row.locked ? true : prefs[row.key as "analytics" | "marketing"];
              return (
                <li key={row.key} className="flex items-start gap-3 rounded-2xl border border-[#ece9f6] px-3.5 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[13.5px] font-bold text-[#140f33]">{row.title}</div>
                    <div className="mt-0.5 text-[12px] leading-snug text-[#6b6788]">{row.body}</div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={on}
                    aria-label={row.title}
                    disabled={row.locked}
                    onClick={() => !row.locked && setPrefs((p) => ({ ...p, [row.key]: !on }))}
                    className={`relative mt-0.5 h-6 w-11 flex-shrink-0 rounded-full transition-colors ${on ? "bg-[#6a52ff]" : "bg-[#d9d5ea]"} ${row.locked ? "opacity-60" : ""}`}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4 sm:grid-cols-[1fr_1fr_auto] sm:items-center [&_button]:touch-manipulation">
          {custom ? (
            <>
              <button type="button" onClick={() => choose(false, false)} className="h-11 rounded-xl border border-[#e2def0] text-[13.5px] font-bold text-[#140f33] hover:bg-[#f7f6fc]">
                Only essential
              </button>
              <button type="button" onClick={() => choose(prefs.analytics, prefs.marketing)} className="h-11 rounded-xl bg-[#140f33] text-[13.5px] font-bold text-white hover:bg-[#241c55]">
                Save choices
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => choose(false, false)} className="h-10 rounded-xl border border-[#e2def0] text-[13px] font-bold text-[#140f33] hover:bg-[#f7f6fc] sm:h-11 sm:text-[13.5px]">
                Reject
              </button>
              <button type="button" onClick={() => choose(true, true)} className="h-10 rounded-xl bg-gradient-to-r from-[#7b61ff] to-[#5a3fe6] text-[13px] font-bold text-white shadow-[0_10px_22px_-10px_rgba(106,82,255,0.9)] sm:h-11 sm:text-[13.5px]">
                Accept all
              </button>
              <button type="button" onClick={() => setCustom(true)} className="col-span-2 -mb-1 flex h-8 items-center justify-center gap-1.5 text-[12px] font-semibold text-[#5a3fe6] sm:col-span-1 sm:mb-0 sm:h-11 sm:rounded-xl sm:px-4 sm:text-[12.5px] sm:hover:bg-[#f4f2fb]">
                <ShieldCheck size={14} /> Choose what to allow
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** A link-style button that reopens the cookie choices (footer, cookie policy). */
export function CookieSettingsButton({ className = "" }: { className?: string }) {
  return (
    <button type="button" onClick={() => window.dispatchEvent(new Event("rb:cookie-settings"))} className={className}>
      Cookie settings
    </button>
  );
}
